import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './UserAvatar.css';

const UserAvatar: React.FC = () => {
  const { user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isClickInsideRef = useRef(false);

  // DOM Analysis function
  const analyzeDOMChain = (element: HTMLElement) => {
    const chain = [];
    let current: HTMLElement | null = element;
    
    while (current) {
      const styles = window.getComputedStyle(current);
      chain.push({
        tag: current.tagName,
        class: current.className,
        id: current.id,
        pointerEvents: styles.pointerEvents,
        zIndex: styles.zIndex,
        position: styles.position,
        overflow: styles.overflow,
        display: styles.display,
        visibility: styles.visibility,
        opacity: styles.opacity,
      });
      current = current.parentElement;
    }
    
    console.log('DOM Chain Analysis:', chain);
    return chain;
  };

  useEffect(() => {
    // Add diagnostic event listeners to capture phase
    const diagnosticHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      console.log(`Event: ${event.type}, Phase: ${event.eventPhase === 1 ? 'CAPTURE' : event.eventPhase === 2 ? 'TARGET' : 'BUBBLE'}, Target:`, target);
      
      if (target.classList?.contains('user-dropdown-item') || target.closest('.user-dropdown-item')) {
        console.log('Click detected on dropdown item!');
        analyzeDOMChain(target);
      }
    };

    if (isDropdownOpen) {
      // Analyze what's at the logout button position
      setTimeout(() => {
        const logoutButton = document.querySelector('.user-dropdown-item.logout') as HTMLElement;
        if (logoutButton) {
          const rect = logoutButton.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          
          console.log('Logout button position:', rect);
          
          // Check what element is at the button's position
          const elementAtPoint = document.elementFromPoint(centerX, centerY);
          console.log('Element at button center:', elementAtPoint);
          
          if (elementAtPoint !== logoutButton && !logoutButton.contains(elementAtPoint)) {
            console.warn('WARNING: Another element is overlapping the logout button!');
            console.log('Overlapping element computed styles:', {
              position: window.getComputedStyle(elementAtPoint as HTMLElement).position,
              zIndex: window.getComputedStyle(elementAtPoint as HTMLElement).zIndex,
            });
            console.log('Dropdown computed styles:', {
              position: window.getComputedStyle(document.querySelector('.user-dropdown') as HTMLElement).position,
              zIndex: window.getComputedStyle(document.querySelector('.user-dropdown') as HTMLElement).zIndex,
            });
            analyzeDOMChain(elementAtPoint as HTMLElement);
          }
        }
      }, 100);

      // Add listeners in both capture and bubble phase for diagnostics
      document.addEventListener('click', diagnosticHandler, true);  // Capture phase
      document.addEventListener('click', diagnosticHandler, false); // Bubble phase
      document.addEventListener('mousedown', diagnosticHandler, true);
      document.addEventListener('mousedown', diagnosticHandler, false);
      
      return () => {
        document.removeEventListener('click', diagnosticHandler, true);
        document.removeEventListener('click', diagnosticHandler, false);
        document.removeEventListener('mousedown', diagnosticHandler, true);
        document.removeEventListener('mousedown', diagnosticHandler, false);
      };
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target as Node)) {
        isClickInsideRef.current = true;
      }
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (!isClickInsideRef.current && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      isClickInsideRef.current = false;
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDropdownOpen]);

  if (!user) return null;

  const getInitials = (name: string | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const userEmail = user.username || 'No email available';
  const userName = user.name || user.username || 'User';

  const handleLogout = async () => {
    console.log('Logout button clicked!');
    try {
      setIsDropdownOpen(false);
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="user-avatar-container" ref={dropdownRef}>
      <button 
        className="user-avatar-button"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-label="User menu"
        aria-expanded={isDropdownOpen}
      >
        <div className="user-avatar">
          <span className="user-avatar-initials">
            {getInitials(userName)}
          </span>
        </div>
        <span className="material-icons dropdown-arrow">
          {isDropdownOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
        </span>
      </button>

      {isDropdownOpen && (
        <div className="user-dropdown">
          <div className="user-dropdown-header">
            <div className="user-dropdown-avatar">
              <span className="user-dropdown-initials">
                {getInitials(userName)}
              </span>
            </div>
            <div className="user-dropdown-info">
              <div className="user-dropdown-name">{userName}</div>
              <div className="user-dropdown-email">{userEmail}</div>
            </div>
          </div>
          
          <div className="user-dropdown-divider"></div>
          
          <div className="user-dropdown-menu">
            <button 
              className="user-dropdown-item" 
              onClick={() => {
                console.log('Profile clicked');
                setIsDropdownOpen(false);
                // Add profile navigation if needed
              }}
            >
              <span className="material-icons">person</span>
              Profile
            </button>
            
            <button 
              className="user-dropdown-item" 
              onClick={() => {
                console.log('Settings clicked');
                setIsDropdownOpen(false);
                // Add settings navigation if needed
              }}
            >
              <span className="material-icons">settings</span>
              Settings
            </button>
          </div>
          
          <div className="user-dropdown-divider"></div>
          
          <div className="user-dropdown-menu">
            <button 
              className="user-dropdown-item logout" 
              onClick={() => {
                console.log('Logout button onClick fired');
                handleLogout();
              }}
            >
              <span className="material-icons">logout</span>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;